/**
 * Guía única, reglas 1.4 y 3.6: "Volver a <lista>" devuelve la lista tal como estaba (vista, filtros,
 * búsqueda). What a list chose lives in its address (rule 3.5); this keeps the last address of each
 * list while the person is away on a detail, so the way back carries it. Held in memory for the tab
 * and in `sessionStorage`, so reloading the detail does not lose it; both are conveniences, never the
 * source of truth: the address is, and a list that was never visited comes back as it opens from
 * the menu (rule 3.5: without filters).
 */
const KEY_PREFIX = 'sl.list.';

const memory = new Map<string, Record<string, unknown>>();

function storageKey(path: string): string {
  return `${KEY_PREFIX}${path}`;
}

/** Keeps the address of `path` (its validated search) as the list shows it now. */
export function rememberList(path: string, search: object): void {
  const kept = Object.fromEntries(
    Object.entries(search).filter(([, value]) => value !== undefined),
  );
  memory.set(path, kept);
  try {
    window.sessionStorage.setItem(storageKey(path), JSON.stringify(kept));
  } catch {
    // A private window, or storage the browser blocked: the memory of the tab still serves.
  }
}

/** The address `path` was last shown with, or an empty search when the list was never visited. */
export function recallList(path: string): Record<string, unknown> {
  const held = memory.get(path);
  if (held !== undefined) {
    return held;
  }

  try {
    const stored = window.sessionStorage.getItem(storageKey(path));
    if (stored !== null) {
      const parsed: unknown = JSON.parse(stored);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    }
  } catch {
    // Unreadable storage is the same as nothing remembered.
  }

  return {};
}

/** Forgets every list: the next person, or the next tenant, starts with its own addresses. */
export function forgetLists(): void {
  memory.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key !== null && key.startsWith(KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Nothing stored, nothing to forget.
  }
}
